/**
 * The Desk: today's edition laid out as a designed front page.
 *
 * This is the app's landing surface, one masthead, one lead image, and the
 * whole round as a numbered typographic grid (front-page tradition: the lead
 * carries the photograph, everything else is type). The daily game and Good
 * News sit inside the grid as instrument cells, and the newsletter lives in
 * the footer, so every rail duplicate has exactly one home here.
 *
 * Honest by construction: numbering follows feed order, so cell "03" is the
 * same story the reader's folio calls "Story 03". Read stories are marked,
 * never hidden.
 */
import { useEffect, useState } from "react";
import { ArrowRight, Check, Play, Share2, Sun, X } from "lucide-react";
import type { InSnipCard } from "../types";
import { resolveImageUrl } from "../assets";
import { displayCaption } from "../caption";
import { buildReceipt, receiptSummary } from "./receipts";
import { mustKnowOf } from "../receipt";

export interface DeskStats {
  stories: number;
  minutes: number;
  sources: number;
}

export interface EditionDeskProps {
  cards: InSnipCard[];
  viewedIds: string[];
  round: number;
  stats: DeskStats;
  essentialId?: string | null;
  goodNewsCount: number;
  gamePlayed: boolean;
  gameAvailable: boolean;
  quizAvailable: boolean;
  /** Estimated read-aloud length of the whole edition, in minutes (0 hides Listen). */
  audioMinutes: number;
  joined: boolean;
  onOpenStory: (id: string) => void;
  onPlayGame: () => void;
  onPlayQuiz: () => void;
  onListen: () => void;
  onGoodNews: () => void;
  onJoin: (email: string) => boolean;
  /** v4: share today's front page as a paste-anywhere text block. */
  onShare: () => void;
  onClose: () => void;
}

const pad2 = (n: number) => String(n).padStart(2, "0");

export function EditionDesk({
  cards,
  viewedIds,
  round,
  stats,
  essentialId,
  goodNewsCount,
  gamePlayed,
  gameAvailable,
  quizAvailable,
  audioMinutes,
  joined,
  onOpenStory,
  onPlayGame,
  onPlayQuiz,
  onListen,
  onGoodNews,
  onJoin,
  onShare,
  onClose,
}: EditionDeskProps) {
  const [email, setEmail] = useState("");
  const [joinState, setJoinState] = useState<"idle" | "invalid" | "done">(joined ? "done" : "idle");

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      // The game / Good News overlays stack above the Desk; their own Escape wins.
      if (document.querySelector(".son-overlay, .goodnews-overlay")) return;
      onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  if (!cards.length) return null;
  const lead = cards[0];
  // Must Know stories lead the grid, rank ascending (the lead plate stays the lead:
  // it carries the photograph). Numbering STAYS the feed position, so cell "03" is
  // still folio Story 03 wherever it sits; only the reading order of the grid moves.
  const rest = [...cards.slice(1, 12)].sort(
    (a, b) => (mustKnowOf(a)?.rank ?? 99) - (mustKnowOf(b)?.rank ?? 99),
  );
  const leadMustKnow = mustKnowOf(lead);
  const leadReceipt = buildReceipt(lead);
  const leadSummary = leadReceipt ? receiptSummary(leadReceipt) : "";
  const readCount = cards.filter((c) => viewedIds.includes(c.id)).length;
  const allRead = readCount >= cards.length;
  const beginLabel = allRead
    ? "Back to the reader"
    : readCount > 0
      ? `Continue reading · ${readCount} of ${cards.length} read`
      : "Begin reading";
  // THE NEXT STORY, BY NAME.
  //
  // "Continue reading, 1 of 10 read" is a status, and a status is not an invitation: it tells
  // someone where they are and leaves them to decide whether there is any reason to go on. The
  // reason is the story itself, so the bar names it. First unread in feed order, which is the
  // order the reader will actually deal them.
  const nextCard = cards.find((c) => !viewedIds.includes(c.id)) ?? null;
  // The spine: the whole round as ten marks. It makes "ten stories, five minutes" an object
  // you can see the end of rather than a claim, shows exactly how far in you are, and every
  // mark opens its own story.
  //
  // Drawn only when it can show the WHOLE round. A truncated spine is worse than none: its
  // entire argument is "here is the end, and it is close", and a row of twelve marks under a
  // ledger reading thirty stories makes that argument falsely. Live rounds are ten; the cap
  // is what keeps a long local or catch-up round from quietly lying about its length.
  const spine = cards.length <= 14 ? cards : [];
  const dateLine = new Date().toLocaleDateString("en-AU", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });
  // The game cell slots in after the first row of stories; Good News near the end.
  const gameSlot = Math.min(3, rest.length);
  const sunSlot = Math.max(gameSlot, rest.length - 2);
  // The puzzles corner sits between the game and the bright side.
  const quizSlot = Math.min(5, Math.max(gameSlot + 2, rest.length - 4));

  const storyCell = (card: InSnipCard, no: number) => {
    const read = viewedIds.includes(card.id);
    const mustKnow = mustKnowOf(card);
    return (
      <button
        key={card.id}
        type="button"
        className={`ed-cell${read ? " read" : ""}`}
        onClick={() => onOpenStory(card.id)}
      >
        <span className="ed-kicker">
          <i className="ed-no">{pad2(no)}</i>
          <span className="ed-kicker-cat">{card.category}</span>
          {mustKnow ? <em className="ed-mustknow">Must know {mustKnow.rank}/3</em> : null}
          {card.id === essentialId ? <em className="ed-essential">Essential</em> : null}
        </span>
        <strong className="ed-cell-head">{card.headline}</strong>
        {mustKnow?.reason ? <span className="ed-mustknow-why">{mustKnow.reason}</span> : null}
        <span className="ed-cell-essence">{card.essence}</span>
        {read ? (
          <span className="ed-read-mark">
            <Check size={11} aria-hidden="true" /> Read
          </span>
        ) : null}
      </button>
    );
  };

  const cells: React.ReactNode[] = [];
  rest.forEach((card, i) => {
    if (i === gameSlot && gameAvailable) {
      cells.push(
        <button key="ed-game" type="button" className="ed-cell ed-game" onClick={onPlayGame}>
          <span className="ed-kicker"><span className="ed-kicker-cat">The daily game</span></span>
          <strong className="ed-cell-head">Signal or Noise</strong>
          <span className="ed-cell-essence">
            {gamePlayed ? "Solved for today. See your calls and score." : "Six headlines. Call real signal or noise."}
          </span>
          <span className="ed-game-go" aria-hidden="true">
            <Play size={13} /> {gamePlayed ? "Your result" : "Play"}
          </span>
        </button>
      );
    }
    if (i === quizSlot && quizAvailable) {
      cells.push(
        <button key="ed-quiz" type="button" className="ed-cell ed-quiz" onClick={onPlayQuiz}>
          <span className="ed-kicker"><span className="ed-kicker-cat">The puzzles corner</span></span>
          <strong className="ed-cell-head">The Front Page Quiz</strong>
          <span className="ed-cell-essence">
            Five facts from today's paper. Match each one to its story.
          </span>
          <span className="ed-game-go" aria-hidden="true">
            <Play size={13} /> Take the quiz
          </span>
        </button>
      );
    }
    if (i === sunSlot && goodNewsCount > 0) {
      cells.push(
        <button key="ed-sun" type="button" className="ed-cell ed-sun" onClick={onGoodNews}>
          <span className="ed-kicker"><span className="ed-kicker-cat">The bright side</span></span>
          <strong className="ed-cell-head">Good News</strong>
          <span className="ed-cell-essence">
            {goodNewsCount === 1 ? "One genuinely good story today." : `${goodNewsCount} genuinely good stories today.`}
          </span>
          <span className="ed-sun-glyph" aria-hidden="true"><Sun size={15} /></span>
        </button>
      );
    }
    // The number is the card's FEED position (folio truth), not its grid slot.
    cells.push(storyCell(card, cards.indexOf(card) + 1));
  });

  const submitJoin = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (onJoin(email)) setJoinState("done");
    else setJoinState("invalid");
  };

  return (
    <section className="edition-desk" role="dialog" aria-modal="true" aria-label="Today's edition">
      <div className="ed-scroll">
        <header className="ed-masthead">
          <div className="ed-topline">
            <span className="ed-date">{dateLine}</span>
            <span className="ed-brand" aria-hidden="true">In<b>Snip</b></span>
            <span className="ed-issue">Round {round} · Pure signal, no noise</span>
          </div>
          <h1 className="ed-title">The Daily Edition</h1>
          <p className="ed-ledger">
            {stats.stories} stories · about {stats.minutes} minutes · every story checked
            {stats.sources > 0 ? ` across ${stats.sources} publications` : ""}
          </p>
          {/* The round, as an object with an end you can see. Read marks are filled, the next
              one to read is lit, and every mark opens its own story. */}
          <div className="ed-spine" role="group" aria-label={`${readCount} of ${cards.length} stories read`}>
            <div className="ed-spine-marks" hidden={!spine.length}>
              {spine.map((c, i) => {
                const read = viewedIds.includes(c.id);
                const isNext = !!nextCard && c.id === nextCard.id;
                return (
                  <button
                    key={c.id}
                    type="button"
                    className={`ed-mark${read ? " read" : ""}${isNext ? " next" : ""}`}
                    onClick={() => onOpenStory(c.id)}
                    aria-label={`Story ${pad2(i + 1)}${read ? ", read" : ""}: ${c.headline}`}
                    title={c.headline}
                  >
                    <i aria-hidden="true" />
                  </button>
                );
              })}
            </div>
            <p className="ed-spine-note">
              {allRead
                ? "Every story read. You are done for today."
                : readCount === 0
                  ? `${cards.length} to read, about ${stats.minutes} minutes, then you are done for the day.`
                  : `${readCount} read, ${cards.length - readCount} to go.`}
            </p>
          </div>

          <div className="ed-mast-acts">
            {audioMinutes > 0 ? (
              <button type="button" className="ed-listen" onClick={onListen}>
                <Play size={11} aria-hidden="true" />
                Listen to this edition
                <i>about {audioMinutes} min, read aloud</i>
              </button>
            ) : null}
            <button type="button" className="ed-listen ed-share" onClick={onShare}>
              <Share2 size={11} aria-hidden="true" />
              Share this front page
            </button>
          </div>
        </header>

        <button type="button" className="ed-lead" onClick={() => onOpenStory(lead.id)}>
          <span className="ed-lead-copy">
            <span className="ed-kicker">
              <i className="ed-no">01</i>
              <span className="ed-kicker-cat">{lead.category}</span>
              {leadMustKnow ? <em className="ed-mustknow">Must know {leadMustKnow.rank}/3</em> : null}
              {lead.id === essentialId ? <em className="ed-essential">Today's essential</em> : null}
            </span>
            <strong className="ed-lead-head">{lead.headline}</strong>
            {leadMustKnow?.reason ? <span className="ed-mustknow-why">{leadMustKnow.reason}</span> : null}
            <em className="ed-lead-essence">{lead.essence}</em>
            <span className="ed-lead-meta">
              {viewedIds.includes(lead.id) ? "Read · " : ""}
              {leadSummary || "Source-checked"}
              <span className="ed-lead-go" aria-hidden="true">Read the story <ArrowRight size={12} /></span>
            </span>
          </span>
          {lead.imageUrl ? (
            <span className="ed-lead-plate" aria-hidden="true">
              {/* Owns "card-media" while the Desk is up: opening the lead morphs this
                  photograph into the reader's plate (the reader defers its name meanwhile). */}
              <img
                src={resolveImageUrl(lead.imageUrl)}
                alt=""
                loading="eager"
                decoding="async"
                style={{ viewTransitionName: "card-media" } as React.CSSProperties}
              />
              {/* No caption on the lead photograph: the stored text is alt text for a screen
                  reader, not an editorial caption. See caption.ts. */}
              {displayCaption(lead.imageAlt) ? (
                <span className="ed-lead-caption">{displayCaption(lead.imageAlt)}</span>
              ) : null}
            </span>
          ) : null}
        </button>

        <div className="ed-grid">{cells}</div>

        <footer className="ed-foot">
          {joinState === "done" ? (
            <p className="ed-join-done">
              <Check size={13} aria-hidden="true" /> You're in. Tomorrow's edition lands in your inbox.
            </p>
          ) : (
            <form className="ed-join" onSubmit={submitJoin}>
              <span className="ed-join-copy">One calm email a day. No noise, no doomscroll.</span>
              <span className="ed-join-row">
                <input
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  required
                  placeholder="you@example.com"
                  aria-label="Email address"
                  aria-invalid={joinState === "invalid"}
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); if (joinState === "invalid") setJoinState("idle"); }}
                />
                <button type="submit">Join</button>
              </span>
              {joinState === "invalid" ? <small className="ed-join-error">Enter a valid email.</small> : null}
            </form>
          )}
          <p className="ed-colophon">Curated, source-checked and set fresh every round.</p>
        </footer>
      </div>

      <div className="ed-bar">
        <span className="ed-bar-note" aria-hidden="true">
          {allRead ? "Every story read. Well done." : `${cards.length - readCount} ${cards.length - readCount === 1 ? "story" : "stories"} waiting`}
        </span>
        {/* The bar opens the NEXT story by name rather than closing the Desk onto whatever the
            reader happened to be on. Naming it is the whole point: a headline is a reason to
            go on, a progress count is not. Falls back to closing when the round is finished. */}
        <button
          type="button"
          className={`ed-begin${nextCard ? " ed-begin-next" : ""}`}
          onClick={() => (nextCard ? onOpenStory(nextCard.id) : onClose())}
        >
          <span className="ed-begin-copy">
            <em>{allRead ? beginLabel : readCount > 0 ? `Next · ${readCount} of ${cards.length} read` : `Start reading · ${cards.length} stories`}</em>
            {nextCard ? <strong>{nextCard.headline}</strong> : null}
          </span>
          <ArrowRight size={16} aria-hidden="true" />
        </button>
      </div>
      <button type="button" className="ed-close" onClick={onClose} aria-label="Close the edition">
        <X size={17} aria-hidden="true" />
      </button>
    </section>
  );
}
